import React from 'react'
import classnames from 'classnames'
import { inject, observer } from 'mobx-react'
import { Tooltip } from 'react-leaflet'
import DynamicGeoJSON from './DynamicGeoJSON'
import PoiTooltip from '../common/PoiTooltip'
import { getShapePopulationFetchOptions, shadeHex, getShapeArea } from '../../utils'
import { selectionTypes } from '../../constants'

const getShapeId = (shape) => shape && (shape.id || shape.inner_id || shape.remote_db_id)

const Geopolygon = ({ uiStore, selectionStore, dataStore, rightSidebarStore, geopolygon, group }) => {
  const shapeId = getShapeId(geopolygon)
  const selectedItemShapeId = getShapeId(selectionStore.item)
  const selectedGroup = selectionStore.data && selectionStore.data.group
  const selectedGroupPk = selectedGroup ? String(selectedGroup.pk) : (selectionStore.item && selectionStore.item.targetGroupId ? String(selectionStore.item.targetGroupId) : null)
  const currentGroupPk = group ? String(group.pk) : (geopolygon.targetGroupId ? String(geopolygon.targetGroupId) : null)

  const isColorEdit = !!(selectionStore.colorEditItem && (
    selectionStore.colorEditItem === geopolygon ||
    (selectionStore.colorEditItem.inner_id && geopolygon.inner_id && String(selectionStore.colorEditItem.inner_id) === String(geopolygon.inner_id) && (!currentGroupPk || String(selectionStore.colorEditItem.targetGroupId) === currentGroupPk)) ||
    (selectionStore.colorEditItem.id && geopolygon.id && String(selectionStore.colorEditItem.id) === String(geopolygon.id) && (!currentGroupPk || String(selectionStore.colorEditItem.targetGroupId) === currentGroupPk))
  ))

  const isSelected = (
    (selectionStore.item === group) ||
    (selectionStore.item === geopolygon) ||
    (geopolygon.inner_id && selectionStore.item && selectionStore.item.inner_id
      ? (String(selectionStore.item.inner_id) === String(geopolygon.inner_id) && (!currentGroupPk || !selectedGroupPk || currentGroupPk === selectedGroupPk))
      : (selectedItemShapeId && shapeId && selectedItemShapeId === shapeId && (!currentGroupPk || !selectedGroupPk || currentGroupPk === selectedGroupPk))) ||
    selectionStore.selectedMenuItems.some(item =>
      item === geopolygon ||
      (item.inner_id && geopolygon.inner_id && String(item.inner_id) === String(geopolygon.inner_id) && (!currentGroupPk || (item.targetGroupId ? String(item.targetGroupId) : selectedGroupPk) === currentGroupPk)) ||
      (getShapeId(item) === shapeId && (!currentGroupPk || (item.targetGroupId ? String(item.targetGroupId) : selectedGroupPk) === currentGroupPk))
    )
  )
  const baseFillOpacity = Math.max(0, Math.min(1, (Number(geopolygon.transparency) || 50) / 100))
  const statusIsOutline = !!geopolygon.status && dataStore.statusColorType === 'outline'
  // In outline mode the stroke shows the status color, but the fill keeps the
  // geometry's own color (color_id / default) instead of the status color.
  const fillBaseColor = statusIsOutline ? geopolygon.baseColor : geopolygon.getColor

  const shouldBringToFront = isSelected || isColorEdit

  const isHovered = uiStore &&
    uiStore.hoveredPolygonId != null &&
    (String(uiStore.hoveredPolygonId) === String(shapeId) ||
      (geopolygon.id != null && String(uiStore.hoveredPolygonId) === String(geopolygon.id)) ||
      (geopolygon.inner_id != null && String(uiStore.hoveredPolygonId) === String(geopolygon.inner_id)) ||
      (geopolygon.remote_db_id != null && String(uiStore.hoveredPolygonId) === String(geopolygon.remote_db_id))) &&
    (!uiStore.hoveredPolygonGroupId || String(uiStore.hoveredPolygonGroupId) === String(currentGroupPk))

  // Resolve population to match RightSidebar's population value for this shape
  const tooltipPopulation = (() => {
    const shapeIdStr = shapeId != null ? String(shapeId) : null

    // 1. If this shape is currently selected in RightSidebarStore (or selectionStore.item)
    const isSelectedInSidebar = rightSidebarStore && (
      (rightSidebarStore.singlePolygonId != null && String(rightSidebarStore.singlePolygonId) === shapeIdStr) ||
      (selectionStore && selectionStore.item && String(getShapeId(selectionStore.item)) === shapeIdStr)
    )

    if (isSelectedInSidebar && rightSidebarStore.totalPopulation != null && rightSidebarStore.totalPopulation > 0) {
      return rightSidebarStore.totalPopulation
    }

    const targetYear = (rightSidebarStore && (rightSidebarStore.populationSelectedYear || rightSidebarStore.latestYear)) ||
      (dataStore && dataStore.defaultYear && dataStore.defaultYear.result) || 2024
    const ds = (rightSidebarStore && rightSidebarStore.populationDataset) ||
      (dataStore && dataStore.defaultDsName) || 'landscan'

    // 2. If singlePolygonData is for this shape
    if (isSelectedInSidebar && rightSidebarStore && rightSidebarStore.singlePolygonData) {
      const stats = rightSidebarStore.singlePolygonData.stats
      if (stats && Array.isArray(stats.total_population) && stats.total_population.length > 0) {
        const match = stats.total_population.find(t => String(t.year) === String(targetYear)) ||
          stats.total_population[stats.total_population.length - 1]
        if (match && match.value != null && match.value > 0) {
          return match.value
        }
      }
    }

    // 3. If rightSidebarStore has polygonData (e.g. target group loaded or multi-selection)
    if (rightSidebarStore && Array.isArray(rightSidebarStore.polygonData) && rightSidebarStore.polygonData.length > 0) {
      const matchedPoly = rightSidebarStore.polygonData.find(p =>
        (p.id != null && String(p.id) === shapeIdStr) ||
        (p.remote_db_id != null && String(p.remote_db_id) === shapeIdStr) ||
        (p.inner_id != null && String(p.inner_id) === shapeIdStr)
      )
      if (matchedPoly) {
        // If this polygon line is specifically selected in the chart
        if (rightSidebarStore.selectedLine != null && String(rightSidebarStore.selectedLine) === shapeIdStr &&
            rightSidebarStore.totalPopulation != null && rightSidebarStore.totalPopulation > 0) {
          return rightSidebarStore.totalPopulation
        }
        if (matchedPoly.trends && matchedPoly.trends.length > 0) {
          const trend = matchedPoly.trends.find(t => String(t.year) === String(targetYear)) ||
            matchedPoly.trends[matchedPoly.trends.length - 1]
          if (trend && trend.population != null && trend.population > 0) {
            return trend.population
          }
        }
        if (matchedPoly.stats && Array.isArray(matchedPoly.stats.total_population) && matchedPoly.stats.total_population.length > 0) {
          const stat = matchedPoly.stats.total_population.find(t => String(t.year) === String(targetYear)) ||
            matchedPoly.stats.total_population[matchedPoly.stats.total_population.length - 1]
          if (stat && stat.value != null && stat.value > 0) {
            return stat.value
          }
        }
      }
    }

    // 4. Check geopolygon.population_datas
    if (geopolygon && geopolygon.population_datas) {
      const pDatas = geopolygon.population_datas
      if (typeof pDatas === 'object' && !Array.isArray(pDatas)) {
        const yearBucket = pDatas[String(targetYear)] || pDatas[targetYear]
        if (Array.isArray(yearBucket) && yearBucket.length > 0) {
          const rec = yearBucket.find(d => (d.type || d.ds_name) === ds) || yearBucket[0]
          if (rec) {
            const val = rec.sum_pop != null ? rec.sum_pop : (rec.sum != null ? rec.sum : (rec.total_population != null ? rec.total_population : rec.value))
            if (val != null && val > 0) return val
          }
        }
        // Try available years (latest first)
        const sortedYears = Object.keys(pDatas).sort().reverse()
        for (const y of sortedYears) {
          const bucket = pDatas[y]
          if (Array.isArray(bucket) && bucket.length > 0) {
            const rec = bucket.find(d => (d.type || d.ds_name) === ds) || bucket[0]
            if (rec) {
              const val = rec.sum_pop != null ? rec.sum_pop : (rec.sum != null ? rec.sum : (rec.total_population != null ? rec.total_population : rec.value))
              if (val != null && val > 0) return val
            }
          }
        }
      } else if (Array.isArray(pDatas) && pDatas.length > 0) {
        const rec = pDatas.find(d => String(d.year || d.ds_year) === String(targetYear) && ((d.type || d.ds_name) === ds || !d.type)) ||
          pDatas.find(d => (d.type || d.ds_name) === ds) ||
          pDatas[0]
        if (rec) {
          const val = rec.sum_pop != null ? rec.sum_pop : (rec.sum != null ? rec.sum : (rec.total_population != null ? rec.total_population : rec.value))
          if (val != null && val > 0) return val
        }
      }
    }

    // 5. Check geopolygon.stats
    if (geopolygon && geopolygon.stats) {
      if (Array.isArray(geopolygon.stats.total_population) && geopolygon.stats.total_population.length > 0) {
        const match = geopolygon.stats.total_population.find(t => String(t.year) === String(targetYear)) ||
          geopolygon.stats.total_population[geopolygon.stats.total_population.length - 1]
        if (match && match.value != null && match.value > 0) return match.value
      }
      if (geopolygon.stats.population != null && geopolygon.stats.population > 0) return geopolygon.stats.population
      if (geopolygon.stats.total_population != null && typeof geopolygon.stats.total_population === 'number' && geopolygon.stats.total_population > 0) return geopolygon.stats.total_population
      if (geopolygon.stats.sum_pop != null && geopolygon.stats.sum_pop > 0) return geopolygon.stats.sum_pop
      if (geopolygon.stats.sum != null && geopolygon.stats.sum > 0) return geopolygon.stats.sum
    }

    // 6. Check geopolygon.analysis
    if (geopolygon && geopolygon.analysis) {
      if (geopolygon.analysis.sum != null && geopolygon.analysis.sum > 0) return geopolygon.analysis.sum
      if (geopolygon.analysis.sum_pop != null && geopolygon.analysis.sum_pop > 0) return geopolygon.analysis.sum_pop
    }

    return null
  })()

  const tooltipArea = (() => {
    if (geopolygon && geopolygon.analysis && geopolygon.analysis.area != null && geopolygon.analysis.area > 0) {
      return geopolygon.analysis.area
    }
    if (geopolygon && geopolygon.stats && (geopolygon.stats.area_sqkm != null || geopolygon.stats.area_km != null)) {
      return geopolygon.stats.area_sqkm || geopolygon.stats.area_km
    }
    const computedArea = getShapeArea(geopolygon)
    if (computedArea && computedArea > 0) {
      return computedArea / 1000000
    }
    return null
  })()

  return (
    <DynamicGeoJSON
      key={`${group ? group.pk : 'shape'}_${geopolygon.inner_id || geopolygon.remote_db_id || geopolygon.id}_${geopolygon.color_id}_${geopolygon.getColor}_${geopolygon.transparency}_${isColorEdit ? 'edit' : 'view'}`}
      data={geopolygon.geoJSON}
      color={geopolygon.getColor}
      fillColor={isSelected ? shadeHex(fillBaseColor, 0.6) : fillBaseColor}
      stroke
      opacity={1}
      weight={isSelected ? 3 : 1}
      bringToFront={shouldBringToFront}
      smoothFactor={geopolygon.smoothFactor}
      className={classnames({
        'with-overlay': uiStore.densityLayer !== 'none',
        geopolygon: true,
        'status-outline': statusIsOutline,
        'leaflet-interactive': !selectionStore.isEditing,
        selected: isSelected
      })}
      onClick={async (e) => {
        // Don't trigger polygon selection if clicking on the tooltip
        if (e.originalEvent && e.originalEvent.target) {
          const target = e.originalEvent.target
          // Check if click is on tooltip or its children
          if (target.closest && typeof target.closest === 'function') {
            const tooltipContainer = target.closest('.poi-tooltip__container')
            if (tooltipContainer) {
              return
            }
          }
        }
        if (!selectionStore.isEditing) {
          const isAlreadySelected = selectionStore.selectedMenuItems.some(item => getShapeId(item) === shapeId)
          const hasCtrlOrMeta = e.originalEvent.ctrlKey || e.originalEvent.metaKey

          if (hasCtrlOrMeta) {
            if (isAlreadySelected) {
              selectionStore.removeSelectedMenuItem(geopolygon)
              if (rightSidebarStore && shapeId) {
                rightSidebarStore.removePolygonFromChart(shapeId)
              }
            } else {
              selectionStore.addSelectedMenuItem(geopolygon)
              if (rightSidebarStore && shapeId) {
                await rightSidebarStore.addPolygonToChart(shapeId, getShapePopulationFetchOptions(geopolygon))
              }
            }
          } else {
            let activeItem = geopolygon
            let activeGroup = group

            if (uiStore && typeof uiStore.scrollToSidebarItem === 'function') {
              const res = uiStore.scrollToSidebarItem(geopolygon, null)
              if (res && res.targetItem && res.targetGroup) {
                activeItem = res.targetItem
                activeGroup = res.targetGroup
              }
            }
            if (uiStore && typeof uiStore.expandTargetGroup === 'function' && activeGroup) {
              uiStore.expandTargetGroup(activeGroup.pk)
            } else if (activeGroup) {
              activeGroup.expanded = true
            }

            const activeShapeId = getShapeId(activeItem)

            selectionStore.clearSelectedMenuItems()
            selectionStore.addSelectedMenuItem(activeItem)
            selectionStore.select(activeItem, { data: { group: activeGroup } })
            if (selectionStore.type !== selectionTypes.GEOPOLYGON && typeof activeItem.loadGeopolygonData === 'function') {
              activeItem.loadGeopolygonData()
            }
            if (uiStore && typeof uiStore.setHoveredPolygon === 'function') {
              uiStore.setHoveredPolygon(activeItem.id || activeItem.remote_db_id || activeItem.inner_id, e, null, activeGroup ? activeGroup.pk : null)
            }

            if (rightSidebarStore && activeShapeId) {
              uiStore.setProcessing(true)
              await rightSidebarStore.fetchDataForSinglePolygon(
                activeShapeId,
                activeItem.name,
                getShapePopulationFetchOptions(activeItem)
              )
              uiStore.setProcessing(false)
            }
          }
        }
      }}
      onMouseOver={(e) => {
        if (!selectionStore.isEditing) {
          if (uiStore && typeof uiStore.setHoveredPolygon === 'function') {
            uiStore.setHoveredPolygon(shapeId, e, null, currentGroupPk)
          }
        }
      }}
      onMouseOut={(e) => {
        // If moving into the tooltip, keep it open
        const related = e && e.originalEvent && e.originalEvent.relatedTarget
        if (related && related.closest && related.closest('.poi-tooltip__container')) {
          return
        }
        if (uiStore && typeof uiStore.clearHoveredPolygon === 'function') {
          uiStore.clearHoveredPolygon()
        }
      }}
      fillOpacity={isSelected ? Math.min(1, baseFillOpacity + 0.1) : baseFillOpacity}
    >
      {isHovered && (
        <Tooltip direction='left' permanent sticky={false}>
          <PoiTooltip
            isRightPaneVisible={uiStore.isPOIMenuExpanded}
            variant='polygon'
            name={geopolygon.name}
            population={tooltipPopulation}
            area={tooltipArea}
            iconId={geopolygon.remote_db_id == null ? 'polygon' : 'hexagon'}
            iconColor={geopolygon.getColor}
            onMouseEnterTooltip={() => {
              if (!uiStore || geopolygon == null) return
              if (typeof uiStore.setHoveredPolygon === 'function') {
                uiStore.setHoveredPolygon(shapeId, null, null, currentGroupPk)
              }
            }}
            onMouseLeaveTooltip={() => {
              if (!uiStore || geopolygon == null) return
              if (typeof uiStore.clearHoveredPolygon === 'function') {
                uiStore.clearHoveredPolygon()
              }
            }}
            onViewMore={async () => {
              selectionStore.select(geopolygon, { data: { group } })
              uiStore.isPOIMenuExpanded = true
              await uiStore.populatePoiResultsForCurrentSelection()
            }}
          />
        </Tooltip>
      )}
    </DynamicGeoJSON>
  )
}

export default inject('uiStore', 'selectionStore', 'dataStore', 'rightSidebarStore')(
  observer(Geopolygon)
)
